import { Grid } from "@mui/material";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetDetentionsQuery } from "../../api/detectionsApi";
import { useGetLumpersQuery } from "../../api/lumpersApi";
import {
  useGetIftaGenerationsQuery,
  useGetIftaProfileQuery,
} from "../../api/reportsApi";
import { useAppSelector } from "../../app/hooks";
import {
  DetentionIcon,
  LumperIcon,
  ReportsIcon,
} from "../../components/common/pageIcons";
import { MenuItemBase } from "../../components/layout/navMenu";
import { UserRoles } from "../../constants/roles";
import { getIftaReportStatusLabel } from "../../utils/statusColors";
import HubGroupPanel, { HubPreviewRow } from "./components/HubGroupPanel";
import HubOverviewSection from "./components/HubOverviewSection";
import { formatShortDate, formatUsd, hasRole, hasShortcut } from "./hubUtils";

interface AccountingHubOverviewProps {
  shortcuts: MenuItemBase[];
}

const CHARGE_ROLES = [
  UserRoles.ADMIN,
  UserRoles.DISPATCHER,
  UserRoles.ACCOUNTING,
];
const IFTA_ROLES = [
  UserRoles.ADMIN,
  UserRoles.ACCOUNTING,
  UserRoles.SAFETY,
  UserRoles.SUPER_ADMIN,
  UserRoles.FLEET,
  UserRoles.IFTA_ADMIN,
];

const PENDING_STATUS = 0;

const AccountingHubOverview: React.FC<AccountingHubOverviewProps> = ({
  shortcuts,
}) => {
  const navigate = useNavigate();
  const role = useAppSelector((state) => state.auth.user)?.role;

  const canCharges =
    hasShortcut(shortcuts, "/detention-lumpers") && hasRole(role, CHARGE_ROLES);
  const canIfta =
    hasShortcut(shortcuts, "/reports/ifta") && hasRole(role, IFTA_ROLES);

  const {
    data: detentions = [],
    isLoading: detentionsLoading,
    isError: detentionsError,
  } = useGetDetentionsQuery(undefined, { skip: !canCharges });
  const {
    data: lumpers = [],
    isLoading: lumpersLoading,
    isError: lumpersError,
  } = useGetLumpersQuery(undefined, { skip: !canCharges });
  const {
    data: iftaProfile,
    isLoading: profileLoading,
    isError: profileError,
  } = useGetIftaProfileQuery(undefined, { skip: !canIfta });
  const {
    data: generations = [],
    isLoading: generationsLoading,
    isError: generationsError,
  } = useGetIftaGenerationsQuery(undefined, { skip: !canIfta });

  const pendingDetentions = useMemo(
    () => detentions.filter((item) => item.status === PENDING_STATUS),
    [detentions],
  );
  const pendingLumpers = useMemo(
    () => lumpers.filter((item) => item.status === PENDING_STATUS),
    [lumpers],
  );

  const detentionSum = pendingDetentions.reduce(
    (sum, item) => sum + (item.amount ?? 0),
    0,
  );
  const lumperSum = pendingLumpers.reduce(
    (sum, item) => sum + (item.amount ?? 0),
    0,
  );
  const detentionNoEmail = pendingDetentions.filter((item) => !item.emailSent);
  const lumperNoEmail = pendingLumpers.filter((item) => !item.emailSent);

  const detentionRows = useMemo<HubPreviewRow[]>(() => {
    return pendingDetentions.map((item) => ({
      id: `det-${item.id}`,
      title: item.loadNumber || `Detention #${item.id}`,
      subtitle: [item.vehicleName, item.trailerUnit].filter(Boolean).join(" · "),
      meta: `${formatUsd(item.amount ?? 0)}${item.emailSent ? "" : " · no email"}`,
      tone: item.emailSent ? "warning" : "error",
      onClick: () => navigate("/detention-lumpers?tab=detentions"),
    }));
  }, [navigate, pendingDetentions]);

  const lumperRows = useMemo<HubPreviewRow[]>(() => {
    return pendingLumpers.map((item) => ({
      id: `lmp-${item.id}`,
      title: item.loadNumber || `Lumper #${item.id}`,
      subtitle: [item.vehicleName, item.trailerUnit].filter(Boolean).join(" · "),
      meta: `${formatUsd(item.amount ?? 0)}${item.emailSent ? "" : " · no email"}`,
      tone: item.emailSent ? "warning" : "error",
      onClick: () => navigate("/detention-lumpers?tab=lumpers"),
    }));
  }, [navigate, pendingLumpers]);

  const latestGenerations = useMemo(
    () =>
      [...generations].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [generations],
  );

  const iftaRows = useMemo<HubPreviewRow[]>(() => {
    const missing = (iftaProfile?.missingFields ?? []).map((field) => ({
      id: `ifta-missing-${field}`,
      title: field,
      subtitle: "Required on the IFTA profile",
      meta: "Missing",
      tone: "warning" as const,
      onClick: () => navigate("/reports/ifta"),
    }));
    const reports = latestGenerations.map((generation) => ({
      id: `ifta-${generation.jobId}`,
      title: generation.quarter,
      subtitle: generation.userName
        ? `${getIftaReportStatusLabel(generation.status)} · ${generation.userName}`
        : getIftaReportStatusLabel(generation.status),
      meta: formatShortDate(generation.createdAt) ?? "",
      tone:
        generation.status.toLowerCase() === "failed"
          ? ("error" as const)
          : generation.status.toLowerCase() === "completed"
            ? ("success" as const)
            : ("info" as const),
      onClick: () => navigate("/reports/ifta"),
    }));
    return [...missing, ...reports];
  }, [iftaProfile?.missingFields, latestGenerations, navigate]);

  const badges: Record<string, number> = {};
  if (canCharges) {
    badges["/detention-lumpers"] = detentionsError || lumpersError
      ? 0
      : pendingDetentions.length + pendingLumpers.length;
  }
  if (canIfta && !profileError) {
    badges["/reports/ifta"] = iftaProfile?.isComplete ? 0 : 1;
  }

  return (
    <HubOverviewSection shortcuts={shortcuts} badges={badges}>
      {canCharges && (
        <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<DetentionIcon />}
            title="Detention"
            href="/detention-lumpers?tab=detentions"
            hrefLabel="Open"
            loading={detentionsLoading}
            emptyText="No pending detention charges."
            counts={[
              {
                label: "pending",
                value: detentionsError ? 0 : pendingDetentions.length,
                tone: "warning",
              },
              {
                label: "open",
                value: detentionsError ? formatUsd(0) : formatUsd(detentionSum),
                tone: "warning",
              },
              {
                label: "no email",
                value: detentionsError ? 0 : detentionNoEmail.length,
                tone: "error",
              },
            ]}
            rows={detentionRows}
          />
        </Grid>
      )}
      {canCharges && (
        <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<LumperIcon />}
            title="Lumper"
            href="/detention-lumpers?tab=lumpers"
            hrefLabel="Open"
            loading={lumpersLoading}
            emptyText="No pending lumper charges."
            counts={[
              {
                label: "pending",
                value: lumpersError ? 0 : pendingLumpers.length,
                tone: "warning",
              },
              {
                label: "open",
                value: lumpersError ? formatUsd(0) : formatUsd(lumperSum),
                tone: "warning",
              },
              {
                label: "no email",
                value: lumpersError ? 0 : lumperNoEmail.length,
                tone: "error",
              },
            ]}
            rows={lumperRows}
          />
        </Grid>
      )}
      {canIfta && (
        <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<ReportsIcon />}
            title="IFTA"
            href="/reports/ifta"
            hrefLabel="Reports"
            loading={profileLoading || generationsLoading}
            emptyText="IFTA profile is complete and there are no recent reports."
            counts={[
              {
                label: "missing fields",
                value: profileError ? 0 : iftaProfile?.missingFields?.length ?? 0,
                tone: "error",
              },
              {
                label: "reports",
                value: generationsError ? 0 : generations.length,
                tone: "neutral",
              },
            ]}
            rows={iftaRows}
          />
        </Grid>
      )}
    </HubOverviewSection>
  );
};

export default AccountingHubOverview;
